/**
 * OPS — Clientes (Lista)
 * Lista todos os clientes com plano, uso e status
 */
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Users, Search, RefreshCw, Building2, Shield, ChevronRight,
  TrendingUp, Video, Cpu, HardDrive, Crown, AlertTriangle, Package
} from 'lucide-react';
import { AZURE } from '../../services/api';
import { COLORS } from '../../components/governance/governanceTheme';
import ClientPlansTab from '../../components/governance/ClientPlansTab';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const CLIENTS_LABELS = {
  pt: {
    title: 'Clientes',
    subtitle: 'Gestão de contas e planos',
    refresh: 'Atualizar',
    tabList: 'Lista',
    tabPlans: 'Planos & Módulos',
    kpiTotal: 'Total',
    kpiActive: 'Ativos',
    kpiTrial: 'Em Trial',
    kpiNoPlan: 'Sem Plano',
    kpiAi: 'AI (mês)',
    kpiVision: 'Vision (mês)',
    searchPlaceholder: 'Buscar por nome ou cidade...',
    allPlans: 'Todos os planos',
    noClients: 'Nenhum cliente encontrado',
    thClient: 'Cliente',
    thPlan: 'Plano',
    thStatus: 'Status',
    thAthletes: 'Atletas',
    thStaff: 'Staff',
    thVideos: 'Vídeos',
    thAi: 'AI',
    thMonthly: 'Mensal',
    thSetup: 'Setup',
    statusLabels: { active: 'Ativo', trialing: 'Trial', past_due: 'Inadimplente', canceled: 'Cancelado', suspended: 'Suspenso' },
    noSubscription: 'Sem plano',
  },
  en: {
    title: 'Clients',
    subtitle: 'Account & plan management',
    refresh: 'Refresh',
    tabList: 'List',
    tabPlans: 'Plans & Modules',
    kpiTotal: 'Total',
    kpiActive: 'Active',
    kpiTrial: 'In Trial',
    kpiNoPlan: 'No Plan',
    kpiAi: 'AI (mo)',
    kpiVision: 'Vision (mo)',
    searchPlaceholder: 'Search by name or city...',
    allPlans: 'All plans',
    noClients: 'No clients found',
    thClient: 'Client',
    thPlan: 'Plan',
    thStatus: 'Status',
    thAthletes: 'Athletes',
    thStaff: 'Staff',
    thVideos: 'Videos',
    thAi: 'AI',
    thMonthly: 'Monthly',
    thSetup: 'Setup',
    statusLabels: { active: 'Active', trialing: 'Trial', past_due: 'Past Due', canceled: 'Canceled', suspended: 'Suspended' },
    noSubscription: 'No plan',
  },
};

const PLAN_META = {
  b2b_starter:     { label: 'Starter',      color: '#6b7280', bg: '#f3f4f6' },
  b2b_pro:         { label: 'Pro',          color: '#b48c2c', bg: '#fef9ee' },
  b2b_performance: { label: 'Performance',  color: '#7c3aed', bg: '#ede9fe' },
  b2b_enterprise:  { label: 'Enterprise',   color: '#111111', bg: '#e5e7eb' },
  starter:         { label: 'Starter',      color: '#6b7280', bg: '#f3f4f6' },
  pro:             { label: 'Pro',          color: '#b48c2c', bg: '#fef9ee' },
  enterprise:      { label: 'Enterprise',   color: '#111111', bg: '#e5e7eb' },
  custom:          { label: 'Custom',       color: '#7c3aed', bg: '#ede9fe' },
};

const STATUS_META = {
  active:   { label: 'Ativo',    color: '#059669', bg: '#d1fae5' },
  trialing: { label: 'Trial',    color: '#d97706', bg: '#fef3c7' },
  past_due: { label: 'Inadimplente', color: '#dc2626', bg: '#fee2e2' },
  canceled: { label: 'Cancelado', color: '#6b7280', bg: '#f3f4f6' },
  suspended:{ label: 'Suspenso',  color: '#dc2626', bg: '#fee2e2' },
};

const PlanBadge = ({ planKey }) => {
  const m = PLAN_META[planKey] || { label: planKey || '—', color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span className="px-2.5 py-1 rounded-full text-xs font-bold" style={{ color: m.color, backgroundColor: m.bg }}>
      {m.label}
    </span>
  );
};

const StatusDot = ({ status }) => {
  const m = STATUS_META[status] || { label: status || 'Sem plano', color: '#9ca3af', bg: '#f3f4f6' };
  return (
    <span className="flex items-center gap-1.5 text-xs font-medium" style={{ color: m.color }}>
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} />
      {m.label}
    </span>
  );
};

const StatCell = ({ icon: Icon, value, label }) => (
  <div className="flex items-center gap-2 text-sm">
    <Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
    <span className="font-bold text-gray-900">{value ?? '—'}</span>
    <span className="text-gray-400 hidden xl:inline">{label}</span>
  </div>
);

const GovernanceClientsPage = () => {
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [viewTab, setViewTab] = useState(searchParams.get('tab') || 'list');

  const { lang } = useGovernanceLang();
  const t = CLIENTS_LABELS[lang] || CLIENTS_LABELS.pt;

  const fetchClients = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (search) params.search = search;
      if (planFilter) params.plan_key = planFilter;
      const { data } = await AZURE.get('/governance/clients', { params });
      setClients(data.data?.clients || data.clients || []);
    } catch (err) {
      console.error('Error loading clients:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchClients();
  };

  const filteredClients = clients.filter(c => {
    if (planFilter && c.plan_key !== planFilter && c.cp_plan_key !== planFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      const name = (c.institution_name || c.trade_name || '').toLowerCase();
      const city = (c.city || '').toLowerCase();
      return name.includes(s) || city.includes(s);
    }
    return true;
  });

  // Summary stats
  const totalClients = clients.length;
  const activeClients = clients.filter(c => c.subscription_status === 'active' || c.cp_plan_active).length;
  const trialClients = clients.filter(c => c.subscription_status === 'trialing').length;
  const noPlanClients = clients.filter(c => !c.plan_key && !c.cp_plan_key).length;
  const mrr = clients.reduce((sum, c) => sum + (c.monthly_price_cents || 0), 0) / 100;
  const totalAiCalls = clients.reduce((sum, c) => sum + (c.agent_calls_month || c.ai_queries || 0), 0);
  const totalVisionCalls = clients.reduce((sum, c) => sum + (c.video_calls_month || c.videos_processed || 0), 0);

  return (
    <div>
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <img src="/vukz-logo.png" alt="Vukz" className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
            <div>
              <h1 className="text-gray-900 text-2xl font-extrabold">{t.title}</h1>
              <p className="text-sm text-gray-500">{t.subtitle}</p>
            </div>
          </div>
          <button onClick={fetchClients} disabled={loading}
            className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 flex items-center gap-2 text-sm">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {t.refresh}
          </button>
        </div>

        {/* View Tabs */}
        <div className="flex gap-2 mb-4">
          <button onClick={() => setViewTab('list')}
            className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all ${viewTab === 'list' ? 'text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            style={viewTab === 'list' ? { backgroundColor: COLORS.primary } : {}}>
            <Building2 size={16} /> {t.tabList}
          </button>
          <button onClick={() => setViewTab('plans')}
            className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all ${viewTab === 'plans' ? 'text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            style={viewTab === 'plans' ? { backgroundColor: COLORS.primary } : {}}>
            <Package size={16} /> {t.tabPlans}
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold" style={{ color: COLORS.primary }}>{totalClients}</div>
            <div className="text-xs text-gray-500">Total</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-700">{activeClients}</div>
            <div className="text-xs text-gray-500">Ativos</div>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-amber-700">{trialClients}</div>
            <div className="text-xs text-gray-500">Em Trial</div>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-red-600">{noPlanClients}</div>
            <div className="text-xs text-gray-500">Sem Plano</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-700">{totalAiCalls.toLocaleString('pt-BR')}</div>
            <div className="text-xs text-gray-500">AI (mes)</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-purple-700">{totalVisionCalls.toLocaleString('pt-BR')}</div>
            <div className="text-xs text-gray-500">Vision (mes)</div>
          </div>
        </div>
      </div>

      {/* Plans & Modules Tab */}
      {viewTab === 'plans' && <ClientPlansTab />}

      {/* List Tab */}
      {viewTab === 'list' && <>
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>
        </form>
        <select
          value={planFilter}
          onChange={(e) => setPlanFilter(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
        >
          <option value="">{t.allPlans}</option>
          <option value="b2b_starter">B2B Starter</option>
          <option value="b2b_pro">B2B Pro</option>
          <option value="b2b_performance">B2B Performance</option>
          <option value="b2b_enterprise">B2B Enterprise</option>
          <option value="starter">Starter (legacy)</option>
          <option value="pro">Pro (legacy)</option>
          <option value="enterprise">Enterprise (legacy)</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.thClient}</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.thPlan}</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.thStatus}</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">{t.thAthletes}</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">{t.thStaff}</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">{t.thVideos}</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">{t.thAi}</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase">{t.thMonthly}</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">{t.thSetup}</th>
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(10)].map((_, j) => (
                      <td key={j} className="px-4 py-4">
                        <div className="h-4 bg-gray-100 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredClients.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-gray-400">
                    {t.noClients}
                  </td>
                </tr>
              ) : (
                filteredClients.map((c) => (
                  <tr key={c.institution_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <Link to={`/clientes/${c.institution_id}`} className="flex items-center gap-3 group">
                        <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-500 flex-shrink-0 overflow-hidden">
                          {c.logo_url ? (
                            <img src={c.logo_url} alt="" className="w-full h-full object-cover" 
                                 onError={(e) => { e.target.style.display = 'none'; }} />
                          ) : (
                            (c.institution_name || '?').substring(0, 2).toUpperCase()
                          )}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 group-hover:text-[#111] text-sm">
                            {c.trade_name || c.institution_name}
                          </div>
                          <div className="text-xs text-gray-400">
                            {[c.city, c.country].filter(Boolean).join(', ') || c.institution_type}
                          </div>
                        </div>
                      </Link>
                    </td>
                    <td className="px-4 py-3"><PlanBadge planKey={c.cp_plan_key || c.plan_key} /></td>
                    <td className="px-4 py-3"><StatusDot status={c.subscription_status} statusLabels={t.statusLabels} /></td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-mono">
                        {c.active_athletes ?? c.athletes_count ?? '—'}
                        {c.athlete_limit ? <span className="text-gray-300">/{c.athlete_limit}</span> : ''}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-mono">
                        {c.active_staff ?? c.staff_count ?? '—'}
                        {c.staff_limit ? <span className="text-gray-300">/{c.staff_limit}</span> : ''}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-mono">
                      {c.video_calls_month ?? c.videos_processed ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-mono">
                      {c.agent_calls_month ?? c.ai_queries ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-mono">
                      {c.monthly_price_cents 
                        ? `R$ ${(c.monthly_price_cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {c.progress_pct != null ? (
                        <div className="flex items-center gap-1.5">
                          <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" 
                                 style={{ width: `${c.progress_pct}%`, backgroundColor: c.progress_pct >= 100 ? '#059669' : COLORS.gold }} />
                          </div>
                          <span className="text-xs text-gray-400">{c.progress_pct}%</span>
                        </div>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/clientes/${c.institution_id}`} 
                            className="text-gray-400 hover:text-gray-900">
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>}
    </div>
  );
};

export default GovernanceClientsPage;
